import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TrendingUp, X } from "lucide-react";
import { api, formatTL, extractErrorMessage } from "@/lib/api";

const nextJan = () => `${new Date().getFullYear() + (new Date().getMonth() >= 9 ? 1 : 0)}-01`;

function PositionRates({ people, rates, fallback, onChange }) {
  const counts = {};
  people.forEach((p) => { const k = p.pozisyon || ""; counts[k] = (counts[k] || 0) + 1; });
  const keys = Object.keys(counts).sort((a, b) => a.localeCompare(b, "tr"));
  return (
    <div className="mt-2 grid sm:grid-cols-3 gap-2 max-h-40 overflow-auto border border-slate-200 rounded-lg p-2" data-testid="bulk-raise-position-rates">
      {keys.map((k) => (
        <label key={k} className="flex items-center gap-2 text-xs">
          <span className="flex-1 truncate font-semibold">{k || "(pozisyonsuz)"} <span className="text-slate-400 font-normal">· {counts[k]}</span></span>
          <input type="number" step="0.01" min="0" value={rates[k] ?? ""} onChange={(e) => onChange(k, e.target.value)}
            placeholder={fallback ? `%${fallback}` : "%"} className="input w-20 text-right" data-testid={`bulk-raise-pos-${k}`} />
        </label>
      ))}
      <p className="sm:col-span-3 text-[11px] text-slate-500">Boş bırakılan pozisyonlara genel oran uygulanır; 0 yazılan pozisyona zam yapılmaz.</p>
    </div>
  );
}

function PersonPicker({ people, picked, q, setQ, onChange }) {
  const norm = (t) => (t || "").toLocaleLowerCase("tr");
  const shown = people.filter((p) => !q || norm(p.ad_soyad).includes(norm(q)) || norm(p.pozisyon).includes(norm(q)));
  const toggle = (id) => { const s = new Set(picked); if (s.has(id)) s.delete(id); else s.add(id); onChange(s); };
  const allShown = shown.length > 0 && shown.every((p) => picked.has(p.id));
  const toggleAll = () => { const s = new Set(picked); shown.forEach((p) => (allShown ? s.delete(p.id) : s.add(p.id))); onChange(s); };
  return (
    <div className="mx-4 mt-2 border border-slate-200 rounded-lg" data-testid="bulk-raise-picker">
      <div className="flex items-center gap-2 p-2 border-b bg-slate-50">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ad veya pozisyon ara..." className="input flex-1 text-xs" data-testid="bulk-raise-search" />
        <button type="button" onClick={toggleAll} className="text-xs font-semibold text-slate-700 px-2 py-1 border rounded" data-testid="bulk-raise-toggle-all">
          {allShown ? "Görünenleri bırak" : "Görünenleri seç"}</button>
        <span className="text-xs text-slate-500">{picked.size} seçili</span>
      </div>
      <ul className="max-h-48 overflow-auto p-1 grid sm:grid-cols-2 gap-x-2">
        {shown.map((p) => (
          <li key={p.id}><label className="flex items-center gap-2 text-xs px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={picked.has(p.id)} onChange={() => toggle(p.id)} data-testid={`bulk-raise-pick-${p.id}`} />
            <span className="font-semibold">{p.ad_soyad}</span><span className="text-slate-400 truncate">{p.pozisyon}</span>
          </label></li>
        ))}
      </ul>
    </div>
  );
}

export function BulkRaiseModal({ selectedIds, onClose, onDone }) {
  const [oran, setOran] = useState("");
  const [ay, setAy] = useState(nextJan());
  const [scope, setScope] = useState(selectedIds.length ? "secili" : "tum");
  const [picked, setPicked] = useState(() => new Set(selectedIds));
  const [people, setPeople] = useState([]);
  const [q, setQ] = useState("");
  const [byPos, setByPos] = useState(false);
  const [posRates, setPosRates] = useState({});
  useEffect(() => {
    api.get("/personnel").then((r) => setPeople((r.data || []).filter((p) => p.aktif !== false && Number(p.aylik_brut) > 0))).catch(() => {});
  }, []);
  const [tamSayi, setTamSayi] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const k = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  const posPayload = byPos ? Object.fromEntries(Object.entries(posRates).filter(([, v]) => v !== "" && v != null).map(([k, v]) => [k, Number(v)])) : null;
  const body = (dry) => ({ oran: Number(oran) || 0, pozisyon_oranlari: posPayload, ay, tam_sayi: tamSayi, dry_run: dry,
    personnel_ids: scope === "secili" ? Array.from(picked) : null });
  const run = async (dry) => {
    if (!(Number(oran) > 0) && !(posPayload && Object.values(posPayload).some((v) => v > 0))) return toast.error("Geçerli bir zam oranı girin");
    if (scope === "secili" && picked.size === 0) return toast.error("En az bir personel seçin");
    if (!dry && !window.confirm(`${preview?.adet || 0} personelin maaşı ${ay} ayından itibaren ${byPos ? "pozisyon oranlarına göre" : `%${oran}`} artırılacak. Onaylıyor musunuz?`)) return;
    setBusy(true);
    try {
      const r = (await api.post("/personnel/bulk-raise", body(dry))).data;
      if (dry) setPreview(r); else { toast.success(`${r.adet} personele %${oran} zam uygulandı`); onDone(); }
    } catch (e) { toast.error(extractErrorMessage(e, "Zam uygulanamadı")); } finally { setBusy(false); }
  };
  const reset = (fn) => (v) => { fn(v); setPreview(null); };
  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 grid place-items-center p-4" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="bulk-raise-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center gap-2">
          <TrendingUp size={16} className="text-emerald-700" /><h3 className="font-bold flex-1">Toplu Zam</h3>
          <button type="button" onClick={onClose} data-testid="bulk-raise-close"><X size={16} /></button>
        </div>
        <div className="p-4 grid sm:grid-cols-4 gap-3 text-sm">
          <label className="block"><span className="text-xs font-semibold text-slate-600">Zam oranı (%)</span>
            <input type="number" step="0.01" value={oran} onChange={(e) => reset(setOran)(e.target.value)} className="input w-full" data-testid="bulk-raise-oran" /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-600">Geçerli olacağı ay</span>
            <input type="month" value={ay} onChange={(e) => reset(setAy)(e.target.value)} className="input w-full" data-testid="bulk-raise-ay" /></label>
          <label className="block"><span className="text-xs font-semibold text-slate-600">Kapsam</span>
            <select value={scope} onChange={(e) => reset(setScope)(e.target.value)} className="input w-full" data-testid="bulk-raise-scope">
              <option value="tum">Tüm aktif personel</option>
              <option value="secili">Personel seç ({picked.size})</option>
            </select></label>
          <label className="flex items-end gap-2 pb-2 text-xs"><input type="checkbox" checked={tamSayi} onChange={(e) => reset(setTamSayi)(e.target.checked)} data-testid="bulk-raise-round" /> Kuruşları yuvarla</label>
        </div>
        <p className="px-4 text-[11px] text-slate-500">Zam, seçilen aydan itibaren bordrolara yansır; önceki aylar eski maaşla kalır. Gelecek bir ay seçerseniz personel kartında o aya kadar mevcut maaş görünür. Maaş tipi (net/brüt) korunur.</p>
        <div className="px-4 pt-2">
          <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
            <input type="checkbox" checked={byPos} onChange={(e) => { setByPos(e.target.checked); setPreview(null); }} data-testid="bulk-raise-by-position" />
            Pozisyona göre farklı oran uygula
          </label>
          {byPos && <PositionRates people={people} rates={posRates} fallback={oran}
            onChange={(k, v) => { setPosRates((r) => ({ ...r, [k]: v })); setPreview(null); }} />}
        </div>
        {scope === "secili" && (
          <PersonPicker people={people} picked={picked} q={q} setQ={setQ}
            onChange={(s) => { setPicked(s); setPreview(null); }} />
        )}
        <div className="p-4 flex-1 overflow-auto">
          {preview && (
            <table className="w-full text-xs" data-testid="bulk-raise-preview">
              <thead><tr className="text-left text-slate-500"><th className="py-1">Personel</th><th>Tip</th><th className="text-right">Oran</th><th className="text-right">Eski</th><th className="text-right">Yeni</th></tr></thead>
              <tbody>{preview.rows.map((r) => (
                <tr key={r.personnel_id} className="border-t border-slate-100"><td className="py-1">{r.ad_soyad}</td><td className="uppercase">{r.maas_tipi}</td><td className="text-right">%{r.oran}</td>
                  <td className="text-right font-mono-tab">{formatTL(r.eski)}</td><td className="text-right font-mono-tab font-semibold text-emerald-700">{formatTL(r.yeni)}</td></tr>))}
              </tbody>
              <tfoot><tr className="border-t font-bold"><td className="py-1">{preview.adet} personel</td><td></td><td></td>
                <td className="text-right">{formatTL(preview.eski_toplam)}</td><td className="text-right text-emerald-700">{formatTL(preview.yeni_toplam)}</td></tr></tfoot>
            </table>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2">
          <button type="button" onClick={() => run(true)} disabled={busy} className="px-4 py-2 border rounded-lg text-sm font-semibold" data-testid="bulk-raise-preview-btn">
            {busy && !preview ? <Loader2 size={14} className="animate-spin inline" /> : "Önizle"}</button>
          <button type="button" onClick={() => run(false)} disabled={busy || !preview || !preview.adet} data-testid="bulk-raise-apply"
            className="px-4 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-sm font-semibold disabled:opacity-40">Zammı Uygula</button>
        </div>
      </div>
    </div>
  );
}
